import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate, BOOKING_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function ReturnPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = React.useState(null);
  const [rented, setRented] = React.useState([]);
  const [returns, setReturns] = React.useState({}); // product_id -> {good:[], damaged:[], missing:[], returned_qty}

  const loadList = React.useCallback(async () => {
    const [d1, d2] = await Promise.all([
      api.get("/bookings", { params: { status: "checked_out" } }),
      api.get("/bookings", { params: { status: "partially_returned" } }),
    ]);
    setRented([...d1.data, ...d2.data]);
  }, []);

  const loadBooking = React.useCallback(async () => {
    if (!id) return;
    const { data } = await api.get(`/bookings/${id}`);
    setB(data);
    const init = {};
    for (const it of data.items || []) init[it.product_id] = { good: [], damaged: [], missing: [], returned_qty: 0 };
    setReturns(init);
  }, [id]);

  React.useEffect(() => { if (!id) loadList(); else loadBooking(); }, [id, loadList, loadBooking]);

  if (!id) {
    return (
      <div className="space-y-4" data-testid="return-index">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Pengembalian</h1>
          <p className="text-sm text-neutral-500">Pilih booking yang sedang disewa untuk diproses pengembaliannya.</p>
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full data-table">
            <thead><tr><th className="text-left">No.</th><th className="text-left">Pelanggan</th><th className="text-left">Project</th><th className="text-left">Kembali</th><th className="text-left">Status</th><th></th></tr></thead>
            <tbody>
              {rented.map((b) => (
                <tr key={b.id}>
                  <td className="font-mono text-xs">{b.number}</td>
                  <td data-testid={`return-list-customer-${b.id}`}>{b.customer_name || "-"}</td>
                  <td>{b.project_name}</td>
                  <td className="text-xs">{formatDate(b.end_date)}</td>
                  <td><StatusBadge statusMap={BOOKING_STATUS} value={b.status} /></td>
                  <td className="text-right"><Link to={`/return/${b.id}`} className="text-green-700 text-xs hover:underline">Proses →</Link></td>
                </tr>
              ))}
              {rented.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-neutral-500">Tidak ada equipment yang sedang disewa.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!b) return <div className="text-sm text-neutral-500">Memuat…</div>;

  const mark = (pid, aid, category) => {
    const cur = returns[pid] || { good: [], damaged: [], missing: [], returned_qty: 0 };
    // remove from all categories then add
    const nx = {
      good: cur.good.filter((x) => x !== aid),
      damaged: cur.damaged.filter((x) => x !== aid),
      missing: cur.missing.filter((x) => x !== aid),
      returned_qty: cur.returned_qty,
    };
    if (category) nx[category] = [...nx[category], aid];
    nx.returned_qty = nx.good.length + nx.damaged.length + nx.missing.length;
    setReturns({ ...returns, [pid]: nx });
  };

  const submit = async (complete = false) => {
    try {
      const payload = {
        returns: Object.entries(returns).map(([pid, v]) => ({
          product_id: pid, ...v,
        })).filter((r) => r.returned_qty > 0),
      };
      await api.post(`/warehouse/return/${id}`, payload);
      if (complete) await api.post(`/warehouse/complete/${id}`);
      toast.success(complete ? "Pengembalian selesai & inspeksi tuntas" : "Pengembalian diproses");
      nav(`/bookings/${id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="return-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">Pengembalian</div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{b.number}</h1>
        <p className="text-sm text-neutral-500">Periksa kondisi setiap aset yang dikembalikan.</p>
      </div>

      {(b.items || []).map((it, idx) => {
        const ret = returns[it.product_id] || { good: [], damaged: [], missing: [] };
        return (
          <div key={idx} className="card-flat p-4" data-testid={`return-item-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{it.product_name}</div>
              <div className="text-xs text-neutral-500">Terpakai: {(it.asset_ids || []).length} unit · Sudah dikembalikan: {it.returned_qty || 0}</div>
            </div>
            <div className="space-y-2">
              {(it.asset_ids || []).map((aid) => {
                const cat = ret.good.includes(aid) ? "good" : ret.damaged.includes(aid) ? "damaged" : ret.missing.includes(aid) ? "missing" : null;
                return (
                  <div key={aid} className="flex items-center gap-2 text-sm">
                    <div className="font-mono text-xs flex-1 truncate">{aid.slice(0, 8)}…</div>
                    <Button size="sm" variant={cat === "good" ? "default" : "outline"} className={cat === "good" ? "bg-green-600 hover:bg-green-700" : ""} onClick={() => mark(it.product_id, aid, "good")} data-testid={`ret-good-${aid}`}>Baik</Button>
                    <Button size="sm" variant={cat === "damaged" ? "default" : "outline"} className={cat === "damaged" ? "bg-amber-600 hover:bg-amber-700 text-white" : ""} onClick={() => mark(it.product_id, aid, "damaged")} data-testid={`ret-damaged-${aid}`}>Rusak</Button>
                    <Button size="sm" variant={cat === "missing" ? "default" : "outline"} className={cat === "missing" ? "bg-red-600 hover:bg-red-700 text-white" : ""} onClick={() => mark(it.product_id, aid, "missing")} data-testid={`ret-missing-${aid}`}>Hilang</Button>
                  </div>
                );
              })}
              {(it.asset_ids || []).length === 0 && (
                <div className="text-xs text-neutral-500">Tidak ada aset yang di-assign untuk produk ini.</div>
              )}
            </div>
          </div>
        );
      })}

      <div className="sticky bottom-0 bg-white border-t border-neutral-200 -mx-6 px-6 py-3 flex justify-end gap-2">
        <Button variant="outline" onClick={() => submit(false)} data-testid="submit-return">Simpan Pengembalian</Button>
        <Button onClick={() => submit(true)} className="bg-green-600 hover:bg-green-700" data-testid="complete-return">Selesai Inspeksi</Button>
      </div>
    </div>
  );
}
